'use client'

import { Button } from '@/components/button'
import { Input } from '@/components/form/input'
import { Label } from '@/components/form/label'
import { Select } from '@/components/form/select'
import { LoanDebtChart } from '@/components/loan/loan-debt-chart'
import { useToast } from '@/hooks/use-toast'
import type {
  Currency,
  LoanType,
  MonthlyPayment,
  PriceMode,
  VATRateKey,
} from '@/utils/loan-calculator'
import { CURRENCY_CONFIG, VAT_RATES } from '@/utils/loan-calculator'
import clsx from 'clsx'
import { Calculator, MousePointerClick } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { AnimatedCurrency } from '../animated-number'
import LoanTypeRadioGroup from './loan-type-radio-group'
import PaymentSchedule from './payment-schedule'
import PriceTypeToggle from './price-type-toggle'

export function LoanCalculator() {
  const { toast } = useToast()

  // Form state
  const [currency, setCurrency] = useState<Currency>('EUR')
  const [calculationCurrency, setCalculationCurrency] =
    useState<Currency>('EUR')
  const [priceMode, setPriceMode] = useState<PriceMode>('gross')
  const [calculationPriceMode, setCalculationPriceMode] =
    useState<PriceMode>('gross')
  const [price, setPrice] = useState<string>('')
  const [formattedPrice, setFormattedPrice] = useState<string>('')
  const [loanTerm, setLoanTerm] = useState<string>('12')
  const [calculationLoanPeriod, setCalculationLoanPeriod] =
    useState<string>('12')
  const [apr, setApr] = useState<string>('10')
  const [calculationApr, setCalculationApr] = useState<string>('10')
  const [initialPayment, setInitialPayment] = useState<string>('')
  const [formattedInitialPayment, setFormattedInitialPayment] =
    useState<string>('')
  const [loanType, setLoanType] = useState<LoanType>('closed')
  const [residualValue, setResidualValue] = useState<string>('0')
  const [formattedResidualValue, setFormattedResidualValue] =
    useState<string>('0')
  const [selectedVatRateKey, setSelectedVatRateKey] = useState<VATRateKey>('EU')
  const [calculationVatRateKey, setCalculationVatRateKey] =
    useState<VATRateKey>('EU')
  const [customVatRate, setCustomVatRate] = useState<string>('0')
  const [calculationCustomVatRate, setCalculationCustomVatRate] =
    useState<string>('0')
  const [totalFinancedAmount, setTotalFinancedAmount] = useState<number>(0)
  const [totalInterestAmount, setTotalInterestAmount] = useState<number>(0)
  const [totalVatAmount, setTotalVatAmount] = useState<number>(0)

  // Compute the current VAT rate directly from form values
  const currentVatRate =
    selectedVatRateKey === 'Custom'
      ? parseFloat(customVatRate) / 100
      : VAT_RATES[selectedVatRateKey].rate

  // Compute the calculation VAT rate - this uses stored values for display consistency
  const calculationVatRate =
    calculationVatRateKey === 'Custom'
      ? parseFloat(calculationCustomVatRate) / 100
      : VAT_RATES[calculationVatRateKey].rate

  // Format input with thousands separator
  const formatNumberInput = (value: string): string => {
    // Remove any non-digit characters except for decimal point
    const numericValue = value.replace(/[^0-9.]/g, '')

    if (numericValue === '') return ''

    // Split by decimal point to handle integer and decimal parts separately
    const parts = numericValue.split('.')
    const integerPart = parts[0]
    const decimalPart = parts.length > 1 ? parts[1] : ''

    // Format integer part with thousands separator based on currency
    const config = CURRENCY_CONFIG[currency]
    const formattedInteger = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      config.thousandsSeparator,
    )

    // Return formatted integer with decimal part if it exists
    return decimalPart
      ? `${formattedInteger}${config.decimalSeparator}${decimalPart}`
      : formattedInteger
  }

  // Parse formatted number back to standard format for calculations
  const parseFormattedNumber = (formattedValue: string): string => {
    if (!formattedValue) return ''
    // Remove all non-digit characters except decimal point
    return formattedValue
      .replace(
        new RegExp(`[^0-9${CURRENCY_CONFIG[currency].decimalSeparator}]`, 'g'),
        '',
      )
      .replace(CURRENCY_CONFIG[currency].decimalSeparator, '.')
  }

  // Handle price change
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const parsedValue = parseFormattedNumber(rawValue)
    setPrice(parsedValue)
    setFormattedPrice(formatNumberInput(parsedValue))
  }

  // Handle initial payment change
  const handleInitialPaymentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const rawValue = e.target.value
    const parsedValue = parseFormattedNumber(rawValue)
    setInitialPayment(parsedValue)
    setFormattedInitialPayment(formatNumberInput(parsedValue))
  }

  const setRawInitialPayment = (rawValue: number | string) => {
    if (typeof rawValue === 'number') {
      rawValue = Math.round(rawValue)
      rawValue = rawValue.toString()
    }
    const parsedValue = parseFormattedNumber(rawValue)
    if (initialPayment === parsedValue) {
      return
    }

    setInitialPayment(parsedValue)
    setFormattedInitialPayment(formatNumberInput(parsedValue))
    handleCalculate()
  }

  // Handle residual value change
  const handleResidualValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const rawValue = e.target.value
    const parsedValue = parseFormattedNumber(rawValue)
    setResidualValue(parsedValue)
    setFormattedResidualValue(formatNumberInput(parsedValue))
  }

  // Update formatted values when currency changes
  useEffect(() => {
    setFormattedPrice(formatNumberInput(price))
    setFormattedInitialPayment(formatNumberInput(initialPayment))
    setFormattedResidualValue(formatNumberInput(residualValue))
  }, [currency])

  // Handle currency change and suggest appropriate VAT rate
  const handleCurrencyChange = (newCurrency: Currency) => {
    setCurrency(newCurrency)
  }

  // Results state
  const [monthlyPayments, setMonthlyPayments] = useState<MonthlyPayment[]>([])
  const [hasCalculated, setHasCalculated] = useState(false)
  const [calculationCount, setCalculationCount] = useState(0)
  const [isPaymentScheduleExpanded, setIsPaymentScheduleExpanded] =
    useState(false)

  // Function to validate our calculation implementation against known values
  const validateCalculation = (
    loanAmount: number,
    years: number,
    aprRate: number,
    expectedMonthlyPayment: number,
    tolerance = 0.05,
  ) => {
    // Calculate monthly interest rate
    const monthlyRate = aprRate / 12 / 100
    // Convert years to months
    const months = years * 12

    // Calculate monthly payment
    const payment =
      (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, months))) /
      (Math.pow(1 + monthlyRate, months) - 1)

    // Calculate error percentage
    const error = Math.abs(
      (payment - expectedMonthlyPayment) / expectedMonthlyPayment,
    )

    // Return true if within tolerance (5% by default)
    return {
      calculated: payment,
      expected: expectedMonthlyPayment,
      isValid: error <= tolerance,
      error: error * 100,
    }
  }

  // Calculate button handler
  const handleCalculate = () => {
    const priceValue = parseFloat(price)
    const loanTermValue = parseInt(loanTerm) // Use current form value directly
    const aprValue = parseFloat(apr) // Keep as percentage for now
    const initialPaymentValue = parseFloat(initialPayment) || 0
    const residualValueValue =
      loanType === 'open' ? parseFloat(residualValue) || 0 : 0

    // Store the current form values so calculations stay consistent even if UI values change
    setCalculationPriceMode(priceMode)
    setCalculationApr(apr)
    setCalculationVatRateKey(selectedVatRateKey)
    setCalculationCustomVatRate(customVatRate)
    setCalculationCurrency(currency)
    setCalculationLoanPeriod(loanTerm)
    setTotalInterestAmount(0)

    if (isNaN(priceValue) || isNaN(loanTermValue) || isNaN(aprValue)) {
      toast({
        variant: 'warning',
        title: 'Please enter valid values',
        description: 'Make sure all fields are filled correctly.',
      })
      return
    }

    // Validate inputs
    if (initialPaymentValue >= priceValue) {
      toast({
        variant: 'destructive',
        title:
          'Initial payment cannot be greater than or equal to the total price',
      })
      return
    }

    if (residualValueValue >= priceValue - initialPaymentValue) {
      toast({
        variant: 'destructive',
        title:
          'Residual value cannot be greater than or equal to the price minus initial payment',
      })
      return
    }

    // Calculate net and gross price based on input mode
    let netAmount: number
    let grossAmount: number

    if (priceMode === 'gross') {
      grossAmount = priceValue
      netAmount = grossAmount / (1 + currentVatRate)
    } else {
      netAmount = priceValue
      grossAmount = netAmount * (1 + currentVatRate)
    }

    // Calculate initial payment in gross and net
    let initialPaymentGross: number
    let initialPaymentNet: number

    if (priceMode === 'gross') {
      initialPaymentGross = initialPaymentValue
      initialPaymentNet = initialPaymentGross / (1 + currentVatRate)
    } else {
      initialPaymentNet = initialPaymentValue
      initialPaymentGross = initialPaymentNet * (1 + currentVatRate)
    }

    // Calculate residual value in gross and net
    let residualValueGross: number
    let residualValueNet: number

    if (priceMode === 'gross') {
      residualValueGross = residualValueValue
      residualValueNet = residualValueGross / (1 + currentVatRate)
    } else {
      residualValueNet = residualValueValue
      residualValueGross = residualValueNet * (1 + currentVatRate)
    }

    const vatOnInitialPayment = initialPaymentGross - initialPaymentNet
    const vatOnResidualValue = residualValueGross - residualValueNet
    const vatOnFinancedAmount =
      grossAmount - netAmount - vatOnInitialPayment - vatOnResidualValue

    setTotalVatAmount(
      vatOnInitialPayment + vatOnResidualValue + vatOnFinancedAmount,
    )

    // Calculate the amount to be financed
    // For net price mode: Calculate based on net amounts
    // For gross price mode: Calculate based on gross amounts
    const financedGrossAmount = grossAmount - initialPaymentGross

    // For closed loans, residual value is part of the financed amount
    // For open loans, residual value is excluded from the financed amount
    const financedNetAmount =
      loanType === 'closed'
        ? netAmount - initialPaymentNet
        : netAmount - initialPaymentNet - residualValueNet

    // Calculate monthly interest rate (yearly rate divided by 12 months)
    // Use the current APR value for calculation
    const monthlyInterestRate = aprValue / 12 / 100

    // For gross price mode, we may need to adjust the interest rate since
    // we're applying it to the gross amount (which includes VAT)
    const effectiveMonthlyInterestRate = monthlyInterestRate

    // Calculate equal monthly payments using the amortization formula
    // PMT = P * [r(1+r)^n] / [(1+r)^n - 1]
    // Where: PMT = monthly payment, P = principal, r = monthly interest rate, n = number of payments

    // For 'net' mode, calculate monthly payment on net amount
    // For 'gross' mode, calculate monthly payment on gross amount
    // This is the amount that will be used for interest calculations
    const principalForCalc =
      priceMode === 'net' ? financedNetAmount : financedGrossAmount
    const monthlyPaymentNet =
      (principalForCalc *
        (effectiveMonthlyInterestRate *
          Math.pow(1 + effectiveMonthlyInterestRate, loanTermValue))) /
      (Math.pow(1 + effectiveMonthlyInterestRate, loanTermValue) - 1)

    // For gross mode, convert back to net for calculation purposes
    const monthlyPaymentForCalc =
      priceMode === 'net'
        ? monthlyPaymentNet
        : monthlyPaymentNet / (1 + currentVatRate)

    // Generate monthly payment schedule
    const payments: MonthlyPayment[] = []

    // Track remaining balances
    // For open loans: principal balance = amount - initialPayment - residualValue
    // For closed loans: principal balance = amount - initialPayment (as residualValue is included in financing)
    let remainingGrossBalance, remainingNetBalance

    if (loanType === 'closed') {
      // For closed loans, residual value is part of the financed amount
      remainingGrossBalance = grossAmount - initialPaymentGross
      remainingNetBalance = netAmount - initialPaymentNet
    } else {
      // For open loans, residual value is excluded from the financed amount
      remainingGrossBalance =
        grossAmount - initialPaymentGross - residualValueGross
      remainingNetBalance = netAmount - initialPaymentNet - residualValueNet
    }

    priceMode === 'gross'
      ? setTotalFinancedAmount(financedGrossAmount)
      : setTotalFinancedAmount(financedNetAmount)

    // Validate our implementation against the example values (can be removed in production)
    if (priceValue === 20000000 && loanTermValue === 240 && aprValue === 8.09) {
      // Note: Validation assumes net mode calculation
      const principalToValidate =
        priceMode === 'net'
          ? 20000000
          : 20000000 / (1 + currentVatRate)
      const validation = validateCalculation(
        principalToValidate,
        20,
        8.09,
        168410,
      )
      // If there's a significant discrepancy, we can log it
      if (!validation.isValid) {
        console.warn(
          `Calculation might be off: Calculated ${validation.calculated.toFixed(2)}, Expected ${validation.expected} (${validation.error.toFixed(2)}% error)`,
        )
      }
    }

    for (let i = 1; i <= loanTermValue; i++) {
      // Calculate interest based on the priceMode
      // For 'net' priceMode, calculate interest on the net amount (for organizations who can deduct VAT)
      // For 'gross' priceMode, calculate interest on the gross amount (for individuals)
      const interestPayment =
        priceMode === 'net'
          ? remainingNetBalance * effectiveMonthlyInterestRate
          : remainingGrossBalance * effectiveMonthlyInterestRate

      // Principal portion is the payment minus the interest
      const netPrincipalPayment =
        priceMode === 'net'
          ? monthlyPaymentForCalc - interestPayment
          : monthlyPaymentForCalc - interestPayment / (1 + currentVatRate)

      // Update net remaining balance
      remainingNetBalance -= netPrincipalPayment

      // Calculate VAT on the principal payment
      const vatPayment = netPrincipalPayment * currentVatRate

      // Calculate gross principal payment
      const grossPrincipalPayment =
        netPrincipalPayment * (1 + currentVatRate)

      // Gross interest payment (interest with VAT for net mode, or already gross for gross mode)
      const grossInterestPayment =
        priceMode === 'net'
          ? interestPayment * (1 + currentVatRate)
          : interestPayment

      // Update gross remaining balance
      remainingGrossBalance -= grossPrincipalPayment

      // Calculate total payment (gross principal + gross interest)
      const monthlyPayment = grossPrincipalPayment + grossInterestPayment
      setTotalInterestAmount(
        (prev) => prev + parseFloat(interestPayment.toFixed(2)),
      )

      payments.push({
        month: i,
        netAmount: parseFloat(netPrincipalPayment.toFixed(2)),
        vatAmount: parseFloat(vatPayment.toFixed(2)),
        grossAmount: parseFloat(grossPrincipalPayment.toFixed(2)),
        interestAmount: parseFloat(interestPayment.toFixed(2)),
        monthlyPayment: parseFloat(monthlyPayment.toFixed(2)),
        // Show the principal balance based on calculation price mode
        principalBalance: parseFloat(
          Math.max(
            0,
            priceMode === 'net'
              ? remainingNetBalance
              : remainingGrossBalance,
          ).toFixed(2),
        ),
      })
    }

    // Add initial payment as month 0 if it's greater than 0
    if (initialPaymentValue > 0) {
      // For initial payment, the principal balance is the total financed amount
      payments.unshift({
        month: 0, // Initial payment (month 0)
        netAmount: parseFloat(initialPaymentNet.toFixed(2)),
        vatAmount: parseFloat(
          (initialPaymentNet * currentVatRate).toFixed(2),
        ),
        grossAmount: parseFloat(initialPaymentGross.toFixed(2)),
        interestAmount: 0, // No interest on initial payment
        monthlyPayment: parseFloat(initialPaymentGross.toFixed(2)),
        // Use the right principal balance based on calculation price mode
        principalBalance: parseFloat(
          (priceMode === 'net'
            ? financedNetAmount
            : financedGrossAmount
          ).toFixed(2),
        ), // Principal Balance after initial payment
      })
    }

    // Add residual value as final payment if it's an open loan and residual value > 0
    if (loanType === 'open' && residualValueValue > 0) {
      payments.push({
        month: loanTermValue + 1, // After the last regular payment
        netAmount: parseFloat(residualValueNet.toFixed(2)),
        vatAmount: parseFloat(
          (residualValueNet * currentVatRate).toFixed(2),
        ),
        grossAmount: parseFloat(residualValueGross.toFixed(2)),
        interestAmount: 0, // No additional interest on residual value
        monthlyPayment: parseFloat(residualValueGross.toFixed(2)),
        principalBalance: 0, // Final payment, so principal balance is 0
      })
    }

    setMonthlyPayments(payments)
    setIsPaymentScheduleExpanded(false) // Reset to collapsed view when recalculating
    setHasCalculated(true)
    setCalculationCount((prevCount) => prevCount + 1) // Increment the calculation counter
  }

  return (
    <div className="space-y-12">
      <div className="rounded-xl bg-gradient-to-t from-gray-50 to-white p-6 md:p-8">
        <div className="flex flex-col gap-6">
          {/* Price Type Toggle */}
          <div className="flex flex-col items-center justify-between sm:flex-row">
            <h2 className="mb-6 text-2xl font-bold">Loan Calculator</h2>

            <div className="relative">
              <PriceTypeToggle
                priceMode={priceMode}
                setPriceMode={setPriceMode}
              />
            </div>
          </div>
          {/* Loan Type Toggle */}
          <LoanTypeRadioGroup loanType={loanType} setLoanType={setLoanType} />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Currency Selection */}
            <div className="md:col-span-1">
              <Label htmlFor="currency" className="mb-2">
                Currency
              </Label>
              <Select
                id="currency"
                name="currency"
                value={currency}
                onChange={(e) =>
                  handleCurrencyChange(e.target.value as Currency)
                }
              >
                <optgroup label="European Currencies">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(([_, config]) => config.region === 'Europe')
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>

                <optgroup label="American Currencies">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(([_, config]) => config.region === 'Americas')
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>

                <optgroup label="Asian Currencies">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(([_, config]) => config.region === 'Asia')
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>

                <optgroup label="Middle East and Africa">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(
                      ([_, config]) =>
                        config.region === 'Middle East and Africa',
                    )
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>

                <optgroup label="Oceania">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(([_, config]) => config.region === 'Oceania')
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>

                <optgroup label="Other">
                  {Object.entries(CURRENCY_CONFIG)
                    .filter(([_, config]) => config.region === 'Other')
                    .map(([code, config]) => (
                      <option key={code} value={code}>
                        {config.name || code} ({code})
                      </option>
                    ))}
                </optgroup>
              </Select>
            </div>

            {/* VAT Rate Selection */}
            <div className="md:col-span-1">
              <div className="mb-2 flex items-center">
                <Label htmlFor="vatRateSelect" className="mb-0 mr-2">
                  VAT Rate
                </Label>
                <div className="group relative">
                  <div className="flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs text-gray-500">
                    ?
                  </div>
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 transform rounded-md bg-gray-800 p-4 text-xs text-white opacity-0 transition group-hover:opacity-100">
                    VAT is applied to the principal portion of each payment. The
                    interest component is calculated based on the net loan
                    amount and then VAT is added to determine the total payment.
                  </div>
                </div>
              </div>
              <Select
                id="vatRateSelect"
                name="vatRateSelect"
                value={selectedVatRateKey}
                onChange={(e) =>
                  setSelectedVatRateKey(e.target.value as VATRateKey)
                }
              >
                {Object.entries(VAT_RATES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </Select>

              {selectedVatRateKey === 'Custom' && (
                <div className="mt-2">
                  <Label htmlFor="customVatRate" className="mb-1">
                    Custom VAT Rate
                  </Label>
                  <Input
                    type="number"
                    name="customVatRate"
                    id="customVatRate"
                    value={customVatRate}
                    onChange={(e) => setCustomVatRate(e.target.value)}
                    placeholder="Enter custom VAT rate"
                    min="0"
                    max="100"
                    step="0.1"
                    trailingIcon="%"
                    className="text-right"
                  />
                </div>
              )}
            </div>
          </div>
          {/* Price Input */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="md:col-span-1">
              <Label htmlFor="price" className="mb-2">
                Financed Amount
              </Label>
              <Input
                type="text"
                name="price"
                id="price"
                value={formattedPrice}
                onChange={handlePriceChange}
                placeholder="0"
                trailingIcon={currency}
                className="text-right"
              />
            </div>
            {/* Initial Payment Input */}
            <div className="md:col-span-1">
              <Label htmlFor="initialPayment" className="mb-2">
                Initial Payment / Deposit {''}
                <span className="text-black/40">(optional)</span>
              </Label>
              <Input
                type="text"
                name="initialPayment"
                id="initialPayment"
                value={formattedInitialPayment}
                onChange={handleInitialPaymentChange}
                placeholder="0"
                trailingIcon={currency}
                className="text-right"
              />
            </div>
            {/* Residual Value Input - only visible for open loans */}

            <div className="md:col-span-1">
              <Label htmlFor="residualValue" className="mb-2">
                Residual Value
              </Label>
              <div className="relative">
                <Input
                  type="text"
                  name="residualValue"
                  id="residualValue"
                  value={formattedResidualValue}
                  onChange={handleResidualValueChange}
                  placeholder="0"
                  trailingIcon={currency}
                  className="text-right"
                  disabled={loanType === 'closed'}
                />
                {loanType === 'closed' && (
                  <>
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-black/30 bg-white blur-sm"></div>
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-600">
                      Only available for Open-End Loans
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Loan Term Input */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="md:col-span-1">
              <Label htmlFor="loanTerm" className="mb-2">
                Loan Term
              </Label>
              <Select
                id="loanTerm"
                name="loanTerm"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              >
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="9">9 months</option>
                <option value="12">1 year (12 months)</option>
                <option value="24">2 years (24 months)</option>
                <option value="36">3 years (36 months)</option>
                <option value="48">4 years (48 months)</option>
                <option value="60">5 years (60 months)</option>
                <option value="72">6 years (72 months)</option>
                <option value="84">7 years (84 months)</option>
                <option value="96">8 years (96 months)</option>
                <option value="108">9 years (108 months)</option>
                <option value="120">10 years (120 months)</option>
                <option value="180">15 years (180 months)</option>
                <option value="240">20 years (240 months)</option>
                <option value="300">25 years (300 months)</option>
                <option value="360">30 years (360 months)</option>
              </Select>
            </div>
            {/* APR Input */}
            <div className="md:col-span-1">
              <Label htmlFor="apr" className="mb-2">
                Annual percentage rate (APR)
              </Label>
              <Input
                type="number"
                name="apr"
                id="apr"
                value={apr}
                onChange={(e) => setApr(e.target.value)}
                placeholder="10"
                step="0.1"
                min="0"
                trailingIcon="%"
                className="text-right"
              />
            </div>
          </div>
          {/* Calculate Button */}
        </div>
        <div
          className={clsx(
            'mt-8 flex w-full flex-wrap gap-y-4',
            hasCalculated ? 'justify-between' : 'justify-end',
          )}
        >
          {hasCalculated && (
            <div className="mt-2 text-xs text-amber-600 transition delay-75 ease-in-out">
              Note: After changing any form value, click the Calculate button to
              update the results.
            </div>
          )}
          <Button
            type="button"
            onClick={handleCalculate}
            className="w-full px-6 py-3 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 focus:ring-offset-white lg:w-fit"
          >
            <Calculator className="mr-2 size-5" />
            Calculate
          </Button>
        </div>
      </div>

      {/* Results Section */}
      {hasCalculated && monthlyPayments.length > 0 && (
        <div className="space-y-6">
          {/* Loan Summary */}
          <div className="rounded-xl bg-white p-6 shadow md:p-8">
            <h2 className="mb-6 text-2xl font-bold">Loan Summary</h2>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">
                  Total Financed Amount
                </div>
                <div className="mt-1 text-xl font-bold">
                  <AnimatedCurrency
                    start={0}
                    end={totalFinancedAmount}
                    currency={calculationCurrency}
                  />
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Total Interest</div>
                <div className="mt-1 text-xl font-bold">
                  <AnimatedCurrency
                    start={0}
                    end={totalInterestAmount}
                    currency={calculationCurrency}
                  />
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Total VAT</div>
                <div className="mt-1 text-xl font-bold">
                  <AnimatedCurrency
                    start={0}
                    end={totalVatAmount}
                    currency={calculationCurrency}
                  />
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Total Repayment</div>
                <div className="mt-1 text-xl font-bold">
                  <AnimatedCurrency
                    start={0}
                    end={
                      totalFinancedAmount +
                      totalInterestAmount +
                      Number(residualValue)
                    }
                    currency={calculationCurrency}
                  />
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Monthly Payment</div>
                <div className="mt-1 text-xl font-bold">
                  <AnimatedCurrency
                    start={0}
                    end={
                      monthlyPayments.find((p) => p.month === 1)
                        ?.monthlyPayment || 0
                    }
                    currency={calculationCurrency}
                  />
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">APR</div>
                <div className="mt-1 text-xl font-bold">{calculationApr}%</div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">VAT Rate</div>
                <div className="mt-1 text-xl font-bold">
                  {(calculationVatRate * 100).toFixed(
                    calculationVatRateKey === 'Custom' ? 2 : 0,
                  )}
                  %
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Loan Term</div>
                <div className="mt-1 text-xl font-bold">
                  {Math.floor(parseInt(calculationLoanPeriod) / 12) > 0
                    ? `${Math.floor(parseInt(calculationLoanPeriod) / 12)} years ${parseInt(calculationLoanPeriod) % 12} months`
                    : `${calculationLoanPeriod} months`}
                </div>
              </div>

              {/* Deposit percentage options */}
              <div className="col-span-2 lg:col-span-4">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  Deposit Options (% of Total Financed Amount)
                </div>
                <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                  {[5, 10, 20, 30].map((percentage) => {
                    const totalFinancedAmount = monthlyPayments.reduce(
                      (sum, payment) => sum + payment.grossAmount,
                      0,
                    )
                    const percentageAmount =
                      (totalFinancedAmount * percentage) / 100

                    return (
                      <button
                        type="button"
                        onClick={() => setRawInitialPayment(percentageAmount)}
                        key={percentage}
                        className="group relative rounded-lg border border-gray-200 bg-white p-3 text-left hover:bg-gray-50 focus:border-black focus:outline-none focus:ring focus:ring-black"
                      >
                        <div className="text-xs text-gray-500">
                          {percentage}% Deposit
                        </div>
                        <div className="mt-1 text-base font-semibold">
                          <AnimatedCurrency
                            start={0}
                            end={percentageAmount}
                            currency={calculationCurrency}
                          />
                          {/* {formatNumber(currency, percentageAmount)} */}
                        </div>
                        <MousePointerClick className="invisible absolute right-2 top-2 size-4 transition delay-75 ease-out group-hover:visible" />
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Schedule */}
          <PaymentSchedule
            currentVatRate={calculationVatRate}
            selectedVatRateKey={calculationVatRateKey}
            currency={calculationCurrency}
            initialPayment={initialPayment}
            residualValue={residualValue}
            monthlyPayments={monthlyPayments}
            isPaymentScheduleExpanded={isPaymentScheduleExpanded}
            setIsPaymentScheduleExpanded={setIsPaymentScheduleExpanded}
            loanTerm={calculationLoanPeriod}
            loanType={loanType}
            priceMode={calculationPriceMode}
          />

          {/* Loan Debt Chart - new component */}
          <LoanDebtChart
            payments={monthlyPayments}
            currency={calculationCurrency}
            calculationKey={calculationCount}
          />
        </div>
      )}
    </div>
  )
}
